import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Drawer - right-side slide-over panel (spec §12).
 * Use for detail views, multi-step forms, scrollable content.
 * For confirmations or short forms (< 5 fields), use <Modal /> instead.
 *
 * Props:
 *   open      - boolean
 *   onClose   - () => void
 *   title     - header text
 *   width     - 'sm' (400) | 'md' (480, default) | 'lg' (600)
 *   footer    - optional React node rendered in a sticky footer
 *   children  - body
 */

const WIDTHS = {
  sm: 'w-[400px]',
  md: 'w-[480px]',
  lg: 'w-[600px]',
};

export default function Drawer({ open, onClose, title, width = 'md', footer, children }) {
  // Esc key handling
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[900] flex justify-end">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative h-full ${WIDTHS[width] || WIDTHS.md} max-w-full bg-white shadow-strong flex flex-col animate-slide-over border-l border-white/40`}
      >
        <header className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200/70">
          <h2 className="text-base font-semibold text-slate-900 tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
          {children}
        </div>
        {footer && (
          <footer className="border-t border-slate-200/70 px-5 sm:px-6 py-3 bg-slate-50/60">
            {footer}
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
}
